// Sprint turn-collapse-final-answer (D8) — the scroll-anchoring contract.
// Sprint compact-action-rows — the contract shrank to the branch that can
// actually fire; the tests shrank with it rather than continuing to
// assert a signature shape production cannot emit (Gate 2 finding).
//
// Sprint work-segment-collapse (PR-B, D8) — the `scrollTop -= delta`
// branch RETURNS, unconditionally (B7 §7 challenger item 6). Its previous
// death was real, not a mistake to re-litigate: the signature carried
// group keys (`agg-<messageId>:<count>`) while the branch looked those
// keys up against `data-turn-id`, which never carried them, so it
// returned early every time — and the case it covered (a NON-tail
// section collapsing on its own) had no producer. Both are fixed here:
// segments now key off `data-segment-id`, emitted by `WorkSegment`'s own
// root (the SAME derivation that builds the signature — no more key
// mismatch); and a mid-turn send makes an earlier section non-last, so
// `deriveTurnMeta`'s `isLastTurn && agentWorking` guard stops holding it
// open and its trailing segment folds the instant the `result` lands —
// the producer this branch needed.
//
// Contract, complete:
//   • The signature changed and the founder was parked at the bottom →
//     stay at the bottom, where the answer is.
//   • The signature changed, the founder scrolled up, and the
//     newly-folded segment(s) are ENTIRELY above the viewport → correct
//     the scroll offset so the element they were reading does not move.
//   • A prepend (`loadOlder`, or conversation search's own load-older
//     button) must not fire either branch — gated on `tailId` UNCHANGED
//     AND `headId` CHANGED (NB-7): tail-only would also swallow a
//     same-render fold+prepend collision, which cannot happen today but
//     costs nothing to guard against explicitly.
//   • Anything else → do nothing.
//
// A manual toggle also cannot fire this: expanding a segment changes
// neither `foldable` nor any group key, so the signature is unchanged
// (`buildCollapseSignature` carries the DERIVED `foldable`, never the
// founder's expanded state — challenger item 5).

import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

export interface UseCollapseAnchorArgs {
  scrollContainerRef: RefObject<HTMLElement | null>;
  /**
   * Stable signature of the action groups AND work segments currently
   * rendered — their keys, sizes, and (for segments) derived `foldable`.
   * Changing it is what fires the anchor (`buildCollapseSignature`,
   * turn-boundaries.ts).
   */
  signature: string;
  /**
   * B3 — ids of segments that ACTUALLY render a `WorkSegment` root this
   * render (`fold === true`), in document order. A bare-size segment
   * folds nothing and emits no `data-segment-id`, so including it here
   * would poison the quorum below.
   */
  foldedSegmentIds: readonly string[];
  /** Tail message id. Unchanged ⇒ candidate prepend. */
  tailId: string | null;
  /** NB-7 — head message id. Changed ⇒ it really was a prepend. */
  headId: string | null;
  /** True when the founder has scrolled away from the bottom. */
  userScrolledUp: boolean;
}

export function useCollapseAnchor({
  scrollContainerRef,
  signature,
  foldedSegmentIds,
  tailId,
  headId,
  userScrolledUp,
}: UseCollapseAnchorArgs): void {
  const prevSigRef = useRef(signature);
  const prevTailRef = useRef(tailId);
  const prevHeadIdRef = useRef(headId);
  const prevFoldedRef = useRef(foldedSegmentIds);
  const prevHeightRef = useRef<number | null>(null);

  // Anchor — declared FIRST, so on the folding commit it still reads the
  // pre-fold height recorded by the effect below (which runs after and
  // then refreshes it for the next commit).
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    const prevSig = prevSigRef.current;
    const prevTail = prevTailRef.current;
    const prevHead = prevHeadIdRef.current;
    const prevFolded = prevFoldedRef.current;
    const prevHeight = prevHeightRef.current;
    prevSigRef.current = signature;
    prevTailRef.current = tailId;
    prevHeadIdRef.current = headId;
    prevFoldedRef.current = foldedSegmentIds;

    if (!el) return;
    if (signature === prevSig) return;
    // NB-7 — skip ONLY when it is provably a prepend: the tail did not
    // move AND the head did. D8 prepend-insensitivity (a `loadOlder`
    // while parked at the bottom must not fight `useInfiniteScrollUp`).
    if (tailId === prevTail && headId !== prevHead) return;

    if (!userScrolledUp) {
      // (a) Parked at the bottom — keep the founder there, the new prose
      // in view.
      el.scrollTop = el.scrollHeight;
      return;
    }

    // (b) Scrolled up: correct ONLY when the newly-folded block is
    // entirely above the viewport. A segment straddling the viewport top
    // is left alone — both edges cannot be preserved at once.
    if (prevHeight === null) return;
    const delta = prevHeight - el.scrollHeight;
    if (delta <= 0) return;
    const justFolded = foldedSegmentIds.filter((id) => !prevFolded.includes(id));
    const cTop = el.getBoundingClientRect().top;
    // B3 — an id with no matching node contributes nothing to the quorum
    // instead of vetoing it (`every` over a missing node returned `false`
    // and silently disabled the branch — the bug that killed it the first
    // time under a DIFFERENT symptom, the `data-turn-id` mismatch above).
    const nodes = justFolded
      .map((id) => el.querySelector(`[data-segment-id="${id}"]`))
      .filter((n): n is Element => n !== null);
    if (nodes.length === 0) return;
    const allAbove = nodes.every((n) => n.getBoundingClientRect().bottom <= cTop);
    if (allAbove) el.scrollTop -= delta;
  }, [signature, foldedSegmentIds, tailId, headId, userScrolledUp, scrollContainerRef]);

  // Height recorder — declared AFTER the anchor effect (the mirror image
  // of `useInfiniteScrollUp.ts`'s own pre/post height bookkeeping). A
  // `ResizeObserver` on the scroll container's content element closes the
  // staleness gap a dep-less layout effect would leave: a height change
  // with no React commit (image load, font swap, `StickyProgressBar`
  // remeasuring) would otherwise leave `prevHeightRef` stale and make
  // `delta` a fiction (B5). `typeof ResizeObserver !== 'undefined'` keeps
  // jsdom green (unimplemented there); jsdom coverage of the delta branch
  // itself comes from B4's explicit `scrollHeight` stubs, not from a
  // real observer firing.
  //
  // Fix (post-build review): observes `[data-testid="message-column"]`
  // explicitly, NOT `el.firstElementChild`. `StickyProgressBar` renders
  // BEFORE the message column and is only conditionally `null` — while a
  // TodoWrite progress bar is active, `firstElementChild` would have been
  // that (fixed-size) bar, and the observer would never fire on a
  // mid-stream fold in that state. Named fallback if the reviewer still
  // rejects the observer approach entirely: keep a render counter,
  // stamp it beside the height, and run the delta branch only when the
  // height was recorded on the immediately preceding commit; otherwise
  // return. Strictly less capable (a legitimate fold after a
  // non-committing resize is skipped) but never wrong.
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const record = () => {
      prevHeightRef.current = el.scrollHeight;
    };
    record();
    const content = el.querySelector('[data-testid="message-column"]');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(record) : null;
    if (ro && content) ro.observe(content);
    return () => ro?.disconnect();
  }, [scrollContainerRef]);
}
